<template>
    <div class="flex flex-column xl:flex-row lg:flex-row md:flex-row px-3 py-2">
        <div class="flex flex-row align-items-center">
            <MultiSelect :modelValue="selectedColumns" :options="columns" optionLabel="header" @update:modelValue="onToggle" display="chip" placeholder="Колонки" size="small" class="border-round-lg border-2" />
            <Button v-if="isUserInGroup('admin')" :label="(screenSize === 'xs' || screenSize === 'sm') ? '' : 'Экспорт'" 
                icon="pi pi-upload" size="small" text @click="exportCSV($event)" severity="info" class="flex"/>
            <Button label="Поиск" icon="pi pi-filter" size="small" @click="loadUsers" class="border-round-lg border-0 ml-2" severity="info"/>
            <Button label="Сбросить" icon="pi pi-filter" size="small" @click="resetFilter" class="border-round-lg border-0 ml-2" severity="info" outlined/>
        </div>
    </div>
    <DataTable v-if="users" :value="users" lazy paginator :rows="20" v-model:filters="filters" ref="dt" dataKey="id"
        :totalRecords="totalRecords" :loading="loading" @page="onPage($event)" @sort="onSort($event)"
        @filter="onFilter($event)" filterDisplay="menu" :globalFilterFields="['id', 'project_id', 'name', 'email', 'referrer', 'telegram', 'date_created', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']" 
        v-model:selection="selectedCustomers" :selectAll="selectAll" @select-all-change="onSelectAllChange"
        @row-select="onRowSelect" @row-unselect="onRowUnselect" 
        :rowsPerPageOptions="[5, 10, 20, 50, 100, 200, 300, 400, 500]">

        <Column field="id" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" :exportable="true">
            <template #header>
                <span class="text-xs">ID</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()"
                    class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <div class="flex flex-row gap-2 align-items-center" v-if="data.id">
                    <span class="text-xs flex my-auto overflow-hidden" style="max-width:4em">
                        <span class="white-space-nowrap" style="text-overflow:ellipsis;">
                            {{ data.id }}
                        </span>
                    </span>...
                    <Button icon="pi pi-copy" @click="copy(data.id)" size="small" class="border-round-md border-round-lg border-0" severity="info" outlined/>
                </div>
            </template>
        </Column>

        <Column field="project_id" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" :exportable="true">
            <template #header>
                <span class="text-xs">PID</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()"
                    class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <div class="flex flex-row gap-2 align-items-center" v-if="data.project_id">
                    <span class="text-xs flex my-auto overflow-hidden" style="max-width:4em">
                        <span class="white-space-nowrap" style="text-overflow:ellipsis;">
                            {{ data.project_id }}
                        </span>
                    </span>...
                    <Button icon="pi pi-copy" @click="copy(data.project_id)" size="small" class="border-round-md border-round-lg border-0" severity="info" outlined/>
                </div>
            </template>
        </Column>

        <Column field="groups" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" :exportable="true">
            <template #header>
                <span class="text-xs">Группы</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()"
                    class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <div class="flex flex-row gap-2 align-items-center text-xs" v-if="data.groups">
                    <span v-for="group in data.groups" :key="group" class="white-space-nowrap" style="text-overflow:ellipsis;">
                        {{ group }}
                    </span>
                </div>
            </template>
        </Column>

        <Column field="name" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" :exportable="false">
            <template #header>
                <span class="text-xs">Имя</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <div class="flex flex-row align-items-center gap-2">
                    <AvatarGroup v-if="data.photo" class="flex">
                        <Avatar :image="data.photo" shape="circle" />
                    </AvatarGroup>
                    <AvatarGroup v-else class="flex">
                        <Avatar icon="pi pi-user" shape="circle" />
                    </AvatarGroup>
                    <span class="text-sm" v-if="data.name">{{ data.name }}</span>
                </div>
            </template>
        </Column>

        <!-- <Column field="login">
            <template #header>
                <span class="text-xs">Логин</span>
            </template>
            <template #body="{ data }">
                <span class="text-sm" v-if="data.login">{{ data.login }}</span>
            </template>
        </Column> -->

        <Column field="tags" v-if="get_column('tag')">
            <template #header>
                <span class="text-xs">Теги</span>
            </template>
            <template #body="{ data }">
                <span v-if="data.tags" v-for="t of data.tags" 
                    class="inline-block py-1 px-2 text-xs border-round-md border-2 font-medium white-space-nowrap" 
                    :class="(getUserTagColor(t)) ? getUserTagColor(t) : 'text-600 surface-border'">
                    {{ getUserTag(t) }}
                </span>
            </template>
        </Column>

        <Column field="sendpulse" :exportable="false">
            <template #header>
                <span class="text-xs">CRMs</span>
            </template>
            <template #body="{ data }">
                <div class="flex flex-row gap-1 align-items-center">
                    <Button v-if="data.sendpulse && data.sendpulse.id" icon="pi pi-comments" iconPos="right"
                        @click="goToSendpulse(data.sendpulse.id)" size="small" class="border-round-md border-round-lg border-none" severity="info" outlined/>
                    <Button v-if="data.notion && data.notion.crm_id" icon="pi pi-id-card" iconPos="right"
                        @click="goToNotion(data.notion.crm_id)" size="small" class="border-round-md border-round-lg border-none" severity="info" outlined/>
                </div>
            </template>
        </Column>
        
        <Column field="telegram" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" :exportable="false">
            <template #header>
                <span class="text-xs">Telegram</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <div class="flex flex-row gap-1 align-items-center" v-if="data.telegram">
                    <span class="flex text-sm">{{data.telegram}}</span>
                    <Button icon="pi pi-telegram" @click="goToTelegram(data.telegram)" size="small" class="border-round-md border-round-lg border-0" severity="info" outlined/>
                    <Button icon="pi pi-copy" @click="copy(data.telegram)" size="small" class="border-round-md border-round-lg border-0" severity="info" outlined/>
                </div>
            </template>
        </Column>

        <Column field="email" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" :exportable="false">
            <template #header>
                <span class="text-xs">Email</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <div class="flex flex-row gap-1 align-items-center" v-if="data.email">
                    <span class="text-sm">{{ data.email }}</span>
                    <Button icon="pi pi-copy" @click="copy(data.email)" size="small" class="border-round-md border-round-lg border-0" severity="info" outlined/>
                </div>
            </template>
        </Column>

        <Column field="referrer" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" :exportable="false">
            <template #header>
                <span class="text-xs">Партнер</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <div class="flex flex-row gap-2 align-items-center" v-if="data.referrer">
                    <span class="flex text-sm">{{get_short_id(data.referrer)}}</span>
                    <Button icon="pi pi-copy" @click="copy(data.referrer)" size="small" class="border-round-md border-round-lg border-0" severity="info" outlined/>
                    <Button icon="pi pi-external-link" @click="detail_page(data.referrer, 'user', true)" size="small" class="border-round-md border-round-lg border-0" severity="info" outlined />
                    <Button icon="pi pi-filter" @click="filterUser(data.referrer)" size="small" class="border-round-md border-round-lg border-0" severity="info" outlined/>
                </div>
            </template>
            <template #footer>
                <span class="text-xs">
                    Партнеров: <br> {{ stat.partner_count }}
                </span>
            </template>
        </Column>

        <Column field="id" :exportable="false">
            <template #body="slotProps">
                <div class="flex flex-row">
                <Button label="Подробно" @click='detail_page(slotProps.data.id, "user", false)' size="small" class="flex border-round-md mr-2" severity="info" />
                <Button label="" @click='detail_page(slotProps.data.id, "user", true)' size="small" icon="pi pi-external-link" text severity="info" class="flex border-round-md"/>
                </div>
            </template>
        </Column>

        <Column field="id" :exportable="false">
            <template #body="slotProps">
                <div class="flex flex-row">
                <Button label="Переводы" @click="openPageWithParams('/admin/transfers/', { user_id: slotProps.data.id })" size="small" class="flex border-round-md mr-2" icon="pi pi-external-link" iconPos="right" severity="info" text/>
                </div>
            </template>
        </Column>

        <Column field="date_created" :exportable="true" sortable filterField="date_created" dataType="date" :showFilterMatchModes="false" :showFilterOperator="false" :showClearButton="true">
            <template #header>
                <span class="text-xs">Создан</span>
            </template>
            <template #filter="{ filterModel }">
                <Calendar v-model="filterModel.value" dateFormat="dd.mm.yy" placeholder="dd.mm.yy" mask="99.99.9999" />
            </template>
            <template #body="{ data }">
                <small class="text-xs white-space-nowrap">{{ formatDate(data.date_created) }}</small>
            </template>
        </Column>

        <Column field="utm_source" v-if="get_column('utm')" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" :exportable="true">
            <template #header>
                <span class="text-xs">utm_source</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs" v-if="data.utm_source">{{ data.utm_source }}</span>
            </template>
            <template #footer>
                <span class="text-xs">
                    utm_source: <br> {{ stat.utm_source_count }}
                </span>
            </template>
        </Column>
        <Column field="utm_medium" v-if="get_column('utm')" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" :exportable="true">
            <template #header>
                <span class="text-xs">utm_medium</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs" v-if="data.utm_medium">{{ data.utm_medium }}</span>
            </template>
            <template #footer>
                <span class="text-xs">
                    utm_medium: <br> {{ stat.utm_medium_count }}
                </span>
            </template>
        </Column>
        <Column field="utm_campaign" v-if="get_column('utm')" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" :exportable="true">
            <template #header>
                <span class="text-xs">utm_campaign</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs" v-if="data.utm_campaign">{{ data.utm_campaign }}</span>
            </template>
            <template #footer>
                <span class="text-xs">
                    utm_campaign: <br> {{ stat.utm_campaign_count }}
                </span>
            </template>
        </Column>
        <Column field="utm_term" v-if="get_column('utm')" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" :exportable="true">
            <template #header>
                <span class="text-xs">utm_term</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs" v-if="data.utm_term">{{ data.utm_term }}</span>
            </template>
            <template #footer>
                <span class="text-xs">
                    utm_term: <br> {{ stat.utm_term_count }}
                </span>
            </template>
        </Column>
        <Column field="utm_content" v-if="get_column('utm')" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" :exportable="true">
            <template #header>
                <span class="text-xs">utm_content</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-xs" v-if="data.utm_content">{{ data.utm_content }}</span>
            </template>
            <template #footer>
                <span class="text-xs">
                    utm_content: <br> {{ stat.utm_content_count }}
                </span>
            </template>
        </Column>

        <template #footer>
            <span class="text-sm">
                Всего {{ totalRecords }} записей.    
            </span>
        </template>

    </DataTable>
    <Skeleton height="2rem" v-else />
</template>

<script>
import Connect from '@/assets/js/Connect'
import Helper from '@/assets/js/Helper'
import { FilterMatchMode, FilterOperator } from '@primevue/core/api';

export default {
    name: 'UsersList',
    data() {
        return {
            users: null,
            totalRecords: 0,
            columns: [
                {field: 'utm', header: 'utm'},
                {field: 'tag', header: 'Теги'},
            ],
            selectedColumns: [],
            load: false,
            filters: {
                id: {value: '', matchMode: 'contains'},
                project_id: {value: '', matchMode: 'contains'},
                name: {value: '', matchMode: 'contains'},
                email: {value: '', matchMode: 'contains'},
                referrer: {value: '', matchMode: 'contains'},
                telegram: {value: '', matchMode: 'contains'},
                date_created: { 
                    operator: FilterOperator.AND, 
                    constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] 
                },
                utm_source: {value: '', matchMode: 'contains'},
                utm_medium: {value: '', matchMode: 'contains'},
                utm_campaign: {value: '', matchMode: 'contains'},
                utm_term: {value: '', matchMode: 'contains'},
                utm_content: {value: '', matchMode: 'contains'},
            },
            sort: {
                field: 'date_created',
                order: -1
            },
            stat: {
                partner_count: 0,
                utm_source_count: null,
                utm_medium_count: null,
                utm_campaign_count: null,
                utm_term_count: null,
                utm_content_count: null,
            },
            dt: null,
        }
    },
    methods: {
        get_column(val) {
            for(const [k,v] of Object.entries(this.selectedColumns)){
                if(v.field == val)return true
            }
        },
        get_short_id(value){
            return this.helper.get_short_id(value)
        },
        copy(text){
            this.helper.copy_clipboard(text)
            this.$toast.add({ severity: 'info', summary: 'Скопировано', detail: text, life: 1000 })
        },
        formatDate(value) {
            return this.helper.format_date(value)
        },
        detail_page(id, page, new_tab = false) {
            if(new_tab){
                const routeData = this.$router.resolve({path: '/admin/'+page+'/' + id});
                window.open(routeData.href, '_blank');
            } else  this.$router.push({ path: '/admin/'+page+'/' + id })
        },
        openPageWithParams(path, params) {
            const routeData = this.$router.resolve({ path, query: params });
            window.open(routeData.href, '_blank');
        },
        goToTelegram(username){
            if(username) window.open('https://t.me/'+username, '_blank')
            return
        },
        filterUser(id){
            this.filters.referrer.value = id
            this.loadUsers()
        },
        onToggle: function (e) {
            if(e.length>0){
                this.selectedColumns = e
            } else {
                this.selectedColumns = []
            }
        },
        onPage(event) {
            this.lazyParams = event
            this.lazyParams.sort = this.sort
            this.loadUsers()
        },
        onSort(event) {
            if(event.sortField && event.sortOrder){
                this.sort = {
                    field: event.sortField,
                    order: event.sortOrder
                }
            } else {
                this.sort = {
                    field: 'date_created',
                    order: -1
                }
            }

            this.lazyParams.sort = this.sort
            this.loadUsers()
        },
        onFilter() {
            this.lazyParams.filters = this.filters
            this.loadUsers()
        },
        onRowSelect() {
            this.selectAll = this.selectedCustomers.length === this.totalRecords
        },
        async loadUsers() {
            this.load = true
            window.scrollTo({ top: 0, behavior: 'smooth' })
            const columnsCodes = this.selectedColumns.map(column => column.field)
            let users = await this.connect.user_list(this.lazyParams.rows, this.lazyParams.first, this.lazyParams.filters, this.lazyParams.sort, columnsCodes).then(res => res)
            if (!users.result || !users.data) {
                this.$toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Ошибка загрузки данных', life: 10000 })
                this.load = false
                return
            }
            this.users = users.data
            console.log(this.users)

            // плоские поля для utm-меток, чтобы они экспортировались как отдельные колонки
            this.users = this.users.map(user => {
                const utm = user.utm || {}
                return {
                    ...user,
                    utm_source: utm.utm_source || null,
                    utm_medium: utm.utm_medium || null,
                    utm_campaign: utm.utm_campaign || null,
                    utm_term: utm.utm_term || null,
                    utm_content: utm.utm_content || null,
                }
            })
            this.totalRecords = users.total
            
            // данные для статистики
            let partner_count = 0
            let utm_source_count = 0
            let utm_medium_count = 0
            let utm_campaign_count = 0
            let utm_term_count = 0
            let utm_content_count = 0

            this.users.forEach(user => {
                if(user.referrer) partner_count++
                if(user.utm){
                    if(user.utm.utm_source) utm_source_count++
                    if(user.utm.utm_medium) utm_medium_count++
                    if(user.utm.utm_campaign) utm_campaign_count++
                    if(user.utm.utm_term) utm_term_count++
                    if(user.utm.utm_content) utm_content_count++
                }
            })
            this.stat.partner_count = partner_count
            this.stat.utm_source_count = utm_source_count
            this.stat.utm_medium_count = utm_medium_count
            this.stat.utm_campaign_count = utm_campaign_count
            this.stat.utm_term_count = utm_term_count
            this.stat.utm_content_count = utm_content_count
            
            this.load = false
        },
        resetFilter(){
            this.filters = {
                id: {value: '', matchMode: 'contains'},
                project_id: {value: '', matchMode: 'contains'},
                name: {value: '', matchMode: 'contains'},
                email: {value: '', matchMode: 'contains'},
                referrer: {value: '', matchMode: 'contains'},
                telegram: {value: '', matchMode: 'contains'},
                date_created: { 
                    operator: FilterOperator.AND, 
                    constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] 
                },
                utm_source: {value: '', matchMode: 'contains'},
                utm_medium: {value: '', matchMode: 'contains'},
                utm_campaign: {value: '', matchMode: 'contains'},
                utm_term: {value: '', matchMode: 'contains'},
                utm_content: {value: '', matchMode: 'contains'},
            }
            this.lazyParams = {
                first: 0,
                rows: 20,
                sortField: null,
                sortOrder: null,
                filters: this.filters,
            }
            this.loadUsers()
        },
        getUserTag(tag){
            return this.helper.GetUserTag(tag)
        },
        getUserTagColor(tag){
            return this.helper.GetUserTagColor(tag)
        },
        getSendpulse(id){
            if(!id) return null
            if(this.users[id] && this.users[id].sendpulse) return this.users[id].sendpulse
            return null
        },
        goToSendpulse(id){
            if(!id) return
            window.open(`https://login.sendpulse.com/chatbots/chats?contact_id=${id}&channel=telegram`, '_blank')
            return
        },
        goToNotion(id){
            if(!id) return
            if(typeof id !== 'string') return
            const str = id.replace(/-/g, '')
            window.open(`https://www.notion.so/pworigin/fe8e29f8f7d84859bb4ccdedb599ebdc?p=${str}`, '_blank')
        },
        isUserInGroup: function (type) { 
            return this.helper.isUserInGroup(type, this.$store.getters.StateGroups)
        },
        exportCSV(){
            this.$refs.dt.exportCSV();
        },
    },
    watch: {

    },
    async beforeCreate() {
        this.connect = new Connect()
        this.helper = new Helper()
    },
    mounted() {
        this.lazyParams = {
            first: 0,
            rows: 20,
            sortField: null,
            sortOrder: null,
            filters: this.filters,
        }
        this.loadUsers()
    }
}
</script>